import { useState, type FormEvent } from 'react';
import { Button, FormField, Card } from '@chikara/ui';
import { isValidCBU, formatCurrency } from '@chikara/utils';
import { useTransfer } from '@/features/transfers/hooks/useTransfer';
import { useAccounts } from '@/features/dashboard/hooks/useAccounts/useAccounts';
import styles from './TransferForm.module.css';

export function TransferForm() {
  const { transfer, validateCbu, isLoading, isValidating, error, success } = useTransfer();
  const { accounts } = useAccounts();
  
  const [formData, setFormData] = useState({
    fromAccountId: '',
    toAccountCbu: '',
    amount: '',
    description: '',
  });
  
  const [cbuInfo, setCbuInfo] = useState<{ holderName?: string; bankName?: string } | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const handleCbuBlur = async () => {
    if (formData.toAccountCbu && isValidCBU(formData.toAccountCbu)) {
      const result = await validateCbu(formData.toAccountCbu);
      if (result.valid) {
        setCbuInfo({ holderName: result.holderName, bankName: result.bankName });
      } else {
        setCbuInfo(null);
        setValidationErrors(prev => ({ ...prev, toAccountCbu: 'CBU inválido' }));
      }
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.fromAccountId) {
      errors.fromAccountId = 'Selecciona una cuenta';
    }

    if (!formData.toAccountCbu) {
      errors.toAccountCbu = 'El CBU es requerido';
    } else if (!isValidCBU(formData.toAccountCbu)) {
      errors.toAccountCbu = 'El CBU no es válido';
    }

    if (!formData.amount) {
      errors.amount = 'El monto es requerido';
    } else if (Number(formData.amount) <= 0) {
      errors.amount = 'El monto debe ser mayor a 0';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (validate()) {
      const success = await transfer({
        fromAccountId: formData.fromAccountId,
        toAccountCbu: formData.toAccountCbu,
        amount: Number(formData.amount),
        description: formData.description,
      });

      if (success) {
        setFormData({
          fromAccountId: '',
          toAccountCbu: '',
          amount: '',
          description: '',
        });
        setCbuInfo(null);
      }
    }
  };

  if (success) {
    return (
      <Card variant="elevated" className={styles.successCard}>
        <Card.Body>
          <div className={styles.successContent}>
            <div className={styles.successIcon}>✓</div>
            <h3>¡Transferencia exitosa!</h3>
            <p>Tu transferencia se ha realizado correctamente.</p>
            <Button onClick={() => window.location.reload()}>
              Realizar otra transferencia
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card variant="elevated">
      <Card.Header>Nueva Transferencia</Card.Header>
      <Card.Body>
        <form className={styles.form} onSubmit={handleSubmit}>
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.field}>
            <label className={styles.label}>Cuenta origen</label>
            <select
              className={styles.select}
              value={formData.fromAccountId}
              onChange={e => setFormData(prev => ({ ...prev, fromAccountId: e.target.value }))}
            >
              <option value="">Selecciona una cuenta</option>
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.alias} - {formatCurrency(account.balance.disposable, { currency: account.currency })}
                </option>
              ))}
            </select>
            {validationErrors.fromAccountId && (
              <span className={styles.fieldError}>{validationErrors.fromAccountId}</span>
            )}
          </div>

          <FormField
            label="CBU destino"
            value={formData.toAccountCbu}
            onChange={e => setFormData(prev => ({ ...prev, toAccountCbu: e.target.value }))}
            onBlur={handleCbuBlur}
            error={validationErrors.toAccountCbu}
            placeholder="0000000000000000000000"
            maxLength={22}
          />

          {isValidating && <span className={styles.validating}>Validando CBU...</span>}
          {cbuInfo && (
            <div className={styles.cbuInfo}>
              <span>{cbuInfo.holderName}</span>
              <span>{cbuInfo.bankName}</span>
            </div>
          )}

          <FormField
            label="Monto"
            type="number"
            value={formData.amount}
            onChange={e => setFormData(prev => ({ ...prev, amount: e.target.value }))}
            error={validationErrors.amount}
            placeholder="0.00"
            min="0"
            step="0.01"
          />

          <FormField
            label="Descripción (opcional)"
            value={formData.description}
            onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Concepto de la transferencia"
          />

          <Button type="submit" isLoading={isLoading} className={styles.submitButton}>
            Transferir
          </Button>
        </form>
      </Card.Body>
    </Card>
  );
}
